import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Share2, Copy, MessageCircle, Loader2, ChevronDown, ChevronUp, Link2, ImageDown } from "lucide-react";
import { buildAvailabilityMessage, buildShareToWhatsAppLink } from "../lib/whatsapp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatTime12(t) {
    const [hh, mm] = t.split(":").map(Number);
    const period = hh >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${period}`;
}

async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    }
}

// Card shown in the staff panel: free slots for the selected date + copy / WhatsApp share.
export const AvailabilityShareCard = ({ dateISO }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [availability, setAvailability] = useState(null);
    const [promoProcs, setPromoProcs] = useState([]);
    const [downloadingImg, setDownloadingImg] = useState(false);

    const handleDownloadImage = async () => {
        setDownloadingImg(true);
        try {
            const res = await axios.get(`${API}/appointments/availability/image.png`, {
                params: { date: dateISO },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "image/png" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `disponibilidad_${dateISO}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Imagen descargada. Envíala por WhatsApp 📷");
        } catch {
            toast.error("No se pudo generar la imagen");
        } finally {
            setDownloadingImg(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        Promise.all([
            axios.get(`${API}/appointments/availability`, { params: { date: dateISO } }),
            axios.get(`${API}/procedures`),
        ])
            .then(([availRes, procRes]) => {
                if (cancelled) return;
                setAvailability(availRes.data);
                setPromoProcs((procRes.data || []).filter((p) => p.promo_price != null));
            })
            .catch(() => {
                if (!cancelled) toast.error("No se pudo cargar la disponibilidad");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, dateISO]);

    const slots = availability?.available_slots || [];
    const message = availability
        ? buildAvailabilityMessage(
              dateISO,
              availability.is_open ? slots : [],
              window.location.origin,
              promoProcs
          )
        : "";

    const handleCopy = async () => {
        const ok = await copyText(message);
        if (ok) toast.success("Disponibilidad copiada. Pégala en WhatsApp 📋");
        else toast.error("No se pudo copiar");
    };

    const handleCopyLink = async () => {
        const ok = await copyText(`${window.location.origin}/disponibilidad`);
        if (ok) toast.success("Link copiado. Guárdalo como respuesta rápida en WhatsApp 🔗");
        else toast.error("No se pudo copiar");
    };

    return (
        <Card className="rounded-2xl border-border bg-card mb-6 overflow-hidden" data-testid="availability-share-card">
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 p-4 hover:bg-muted/50 transition-colors"
                data-testid="availability-share-toggle"
            >
                <span className="flex items-center gap-2.5 font-medium text-sm">
                    <span className="w-8 h-8 rounded-lg bg-[#25D366]/10 flex items-center justify-center">
                        <Share2 className="h-4 w-4 text-[#25D366]" />
                    </span>
                    Compartir disponibilidad por WhatsApp
                </span>
                {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>

            {open && (
                <div className="px-4 pb-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Consultando horarios libres...
                        </div>
                    ) : !availability ? null : !availability.is_open ? (
                        <p className="text-sm text-muted-foreground py-2" data-testid="availability-share-closed">
                            Este día el consultorio está cerrado.
                        </p>
                    ) : (
                        <>
                            {slots.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-2" data-testid="availability-share-full">
                                    No quedan horarios libres para este día.
                                </p>
                            ) : (
                                <div className="flex flex-wrap gap-1.5 py-2" data-testid="availability-share-slots">
                                    {slots.map((s) => (
                                        <span
                                            key={s}
                                            className="text-xs font-medium tabular-nums px-2.5 py-1 rounded-full bg-primary/10 text-primary"
                                        >
                                            {formatTime12(s)}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="flex flex-wrap gap-2 mt-3">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleCopy}
                                    className="rounded-xl"
                                    data-testid="availability-copy-btn"
                                >
                                    <Copy className="h-3.5 w-3.5 mr-1.5" />
                                    Copiar mensaje
                                </Button>
                                <a
                                    href={buildShareToWhatsAppLink(message)}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="availability-whatsapp-btn"
                                >
                                    <Button size="sm" className="rounded-xl bg-[#25D366] hover:bg-[#1fb958] text-white">
                                        <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
                                        Enviar por WhatsApp
                                    </Button>
                                </a>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleDownloadImage}
                                    disabled={downloadingImg}
                                    className="rounded-xl"
                                    data-testid="availability-download-image-btn"
                                >
                                    {downloadingImg ? (
                                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                    ) : (
                                        <ImageDown className="h-3.5 w-3.5 mr-1.5" />
                                    )}
                                    Descargar imagen
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleCopyLink}
                                    className="rounded-xl"
                                    data-testid="availability-copy-link-btn"
                                >
                                    <Link2 className="h-3.5 w-3.5 mr-1.5" />
                                    Copiar link /disponibilidad
                                </Button>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-2">
                                El mensaje incluye los horarios libres de este día, el link para reservar y el link de disponibilidad de otros días.
                            </p>
                        </>
                    )}
                </div>
            )}
        </Card>
    );
};
